import { union, unionWith } from 'lodash';
import { action, computed, IReactionDisposer, observable, reaction, toJS, when } from 'mobx';
import {
    asStringOrEmpty,
    bind,
    IConfig,
    IDisposable,
    IInputMultiSelectFiltersMeta,
    IMultiSelectOption,
    mobxEqual,
    Mutex,
    Optional,
    RetryPolicy,
    TasksStore,
    undefinedIfEmpty,
    uuidv4generate,
} from 'Mri.Web.Common';
import { parse } from 'qs';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { displayFilters } from './displayFilters';
import { defaultDisplayFilter, DisplayFiltersStore } from './DisplayFiltersStore';
import { DisplayViewTypes } from './DisplayViewSelector';
import { IWhereClause } from './WhereFilter';
import { AccountStore, defaultAggregateColumns, defaultTableColumns } from '../Account/AccountStore';
import {ContractsStore} from "../Contracts/ContractsStore";
import {AccountsStore} from "../Accounts/AccountsStore";
import {displayTelemetryCells} from "../Displays/DisplayCells_Telemetry";
import { displayAggregateCells } from '../Displays/DisplayCells_Aggregate';
import {IDisplayCellOffset} from "../Displays/DisplayTableHeader";

export interface IDisplayListViewModelOptions {
    onChange?(
        displayFilter: Mri.DisplayFilters.DisplayFilter,
        displayEventFilter: Mri.DisplayEvents.DisplayEventFilter | undefined,
        sort: string[],
        where: IWhereClause[] | undefined,
        columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[],
        viewType: DisplayViewTypes
    ): void;
    onClear?(): void;
    readonly defaultDisplayFilter?: Optional<Mri.DisplayFilters.DisplayFilter>;
    readonly displayFilter?: Optional<Mri.DisplayFilters.DisplayFilter>;
    readonly exceptDisplayFilter?: Optional<Mri.DisplayFilters.DisplayFilter>;
    readonly disabledFilters?: string[];
    readonly displayEventFilter?: Mri.DisplayEvents.DisplayEventFilter;
    readonly sort?: string[];
    readonly favorites?: boolean;
    readonly where?: IWhereClause[];
    readonly columns?: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    readonly removeCheckedOnChange?: boolean;
    readonly viewType?: DisplayViewTypes;
    readonly interval?: number;
    readonly subscribe?: boolean;
    readonly showStickyColumns?: boolean;
}

export interface IDisplayListViewModelStores {
    readonly displaysStore: DisplaysStore;
    readonly displayFiltersStore: DisplayFiltersStore;
    readonly tasksStore: TasksStore;
    readonly accountStore: AccountStore;
    readonly contractsStore: ContractsStore;
    readonly accountsStore: AccountsStore;
}

export class DisplayListViewModel implements IDisposable {
    public static stores: IDisplayListViewModelStores;

    public constructor(options: IDisplayListViewModelOptions) {
        this.reportChange = options.onChange;
        this.reportClear = options.onClear;
        this.defaultDisplayFilter = defaultDisplayFilter;
        this.displayFilter = this.parseDisplayFilter(options.displayFilter);
        this.filters = this.generateFilters();
        this.disabledFilters = options.disabledFilters ?? [];

        if (options.exceptDisplayFilter)
            this.exceptDisplayFilter = this.parseDisplayFilter(options.exceptDisplayFilter);

        this.displayEventFilter = options.displayEventFilter;
        this.sort = options.sort ?? [];
        this.where = options.where;

        this.favorites = options.favorites ?? false;
        this.subscribe = options.subscribe;

        this.columns = toJS(options.columns) ?? [];
        this.stickyColumns = DisplayListViewModel.stores.accountStore.storage.displayTableStickyColumns ?? [];
        this.stickyOffsets = [];
        this.showStickyColumns = options.showStickyColumns;
        
        this.removeCheckedOnChange = options.removeCheckedOnChange;
        this.viewType = options.viewType ?? DisplayViewTypes.TableLarge;
        this.selectAllColumnsChecked =
            this.columns.length ==
            (this.viewType == DisplayViewTypes.Aggregate ? displayAggregateCells.length : displayTelemetryCells.length);

        this.reactionDisposers.push(reaction(() => toJS(this.displayFilter), this.tryDeferDisplayFilterChange));
        this.reactionDisposers.push(reaction(() => toJS(this.displayEventFilter), this.tryDeferDisplayFilterChange));
        this.reactionDisposers.push(reaction(() => toJS(this.sort), this.tryDeferDisplayFilterChange));
        this.reactionDisposers.push(reaction(() => toJS(this.where), this.tryDeferDisplayFilterChange))
        this.reactionDisposers.push(reaction(() => toJS(this.status), this.tryDeferDisplayFilterChange));
        this.reactionDisposers.push(reaction(() => toJS(this.telemetry), this.tryDeferDisplayFilterChange));
        this.reactionDisposers.push(reaction(() => toJS(this.viewType), this.tryDeferDisplayFilterChange));
        this.reactionDisposers.push(reaction(() => this.filterOpen,()=> this.handleDeferredDisplayFilterChange()));
        if (this.reportChange)
            this.reactionDisposers.push(
                reaction(
                    () => toJS(this.columns),
                    () =>
                        this.reportChange!(
                            this.displayFilter,
                            this.displayEventFilter,
                            this.sort,
                            this.where,
                            this.columns,
                            this.viewType,
                        )
                )
            );
        if (this.subscribe)
            this.reactionDisposers.push(reaction(() => this.serialNumbers, this.subscribeDisplaysAsync));
        if (this.subscribe && this.interval) this.intervalHandle = window.setTimeout(this.refreshAsync);
    }

    @bind
    @action
    public async loadAllOptionsAsync() {
        const p1 = this.optionsRetryPolicy.execute(() =>
            DisplayListViewModel.stores.displayFiltersStore.loadAllFilterOptionsAsync()
        );
        const p2 = this.optionsRetryPolicy.execute(() => this.loadOptionsAsync());
        try {
            await Promise.all([p1, p2]);
        } catch (e: any) {
            console.log('loadAllOptionsAsync', e);
        }
    }

    @bind
    @action
    public async loadOptionsAsync() {
        const displayFilter = {
            ...this.displayFilter,
            textSearch: this.displayFilter.textSearch || undefined,
        };
        try {
            await this.optionsRetryPolicy.execute(() =>
                DisplayListViewModel.stores.displayFiltersStore.loadFilterOptionsAsync(displayFilter)
            );
        } catch (e: any) {
            console.error('loadOptionsAsync', e);
        }
        this.filterOptions = DisplayListViewModel.stores.displayFiltersStore!.filterOptions.get(
            JSON.stringify(displayFilter)
        );
    }

    @bind
    @action
    public async loadMoreAsync(limit?: number) {
        if (this.error) return;

        if (this.serialNumbers.length == this.total) return;

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }

        await this.loadDisplaysLock.waitAsync();
        this.busy = true;
        this.abortController = new AbortController();
        try {
            this.scrollCount += 40;

            const search: Mri.Web.Portal.Controllers.Displays.SearchModel = {
                displayFilter: {
                    ...this.displayFilter,
                    textSearch: this.displayFilter.textSearch || undefined,
                },
                exceptDisplayFilter: this.exceptDisplayFilter,
                displayEventFilter: this.displayEventFilter,
                limit: limit ?? this.limit,
                offset: this.serialNumbers.length,
                orderBy: this.sort.join(','),
                searchGuid: this.searchGuid,
                status: true,
                telemetry: true,
                where: this.where?.map(w => '( ' + w.value + ' )').join(' && ') ?? '',
                favorites: this.favorites,
                aggregates: this.aggregates,
                startDate: this.startDate,
                endDate: this.endDate,
            };
            const config: IConfig = {
                signal: this.abortController.signal,
            };
            const response = await this.retryPolicy.execute(() =>
                DisplayListViewModel.stores.displaysStore.loadWhereSearchAsync(search, config)
            );
            if (response) {
                this.total = response.total;
                this.serialNumbers = union(this.serialNumbers, response.result);
            }
        } catch (e: any) {
            if (e.name != 'AbortError') {
                console.error(e);
                this.error = e;
                DisplayListViewModel.stores.tasksStore.toastErrorTask('error', e);
            }
        } finally {
            this.busy = false;
            this.abortController = undefined;
            this.loadDisplaysLock.release();
        }
    }
    @bind
    @action
    public async loadContractsAsync() {
        try {
            await DisplayListViewModel.stores.contractsStore.loadAllAsync(true);
        } catch (e: any) {
            console.error('Contracts Failed to Load', e);
        }
    }

    @bind
    @action
    public async loadAccountsAsync() {
        try {
            await DisplayListViewModel.stores.accountsStore.loadAsync();
        } catch (e: any) {
            console.error('Accounts Failed to Load', e);
        }
    }

    @bind
    @action
    public async loadGpsAsync() {
        await DisplayListViewModel.stores.displaysStore.loadGpsDataAsync(this.gpsSearch);
    }

    public async reloadAsync() {
        await this.onDisplayFilterChange();
    }

    @bind
    @action
    public onSearchChange(value: string) {
        // Regular expression to match serial numbers separated by commas
        const serialNumberPattern = /(\w+)/g;

        // Extract serial numbers from the input value
        const serialNumbers = value.match(serialNumberPattern);

        // Set the serial numbers to the display filter
        if (serialNumbers) {
            if(serialNumbers.length > 1) {
                this.displayFilter.textSearch = undefined;
                this.displayFilter.displays = serialNumbers;
            }
            if(serialNumbers.length == 1) {
                this.displayFilter.displays = undefined;
                this.displayFilter.textSearch = value;
            }
        } else {
            // Set the text search value
            this.displayFilter.displays = undefined;
            this.displayFilter.textSearch = value;
        }
    }

    @bind
    @action
    public onColumnChange(value: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] | null | undefined) {
        this.columns = value ?? [];

        const fields = this.columns
            .map(c => {
                const cell = displayTelemetryCells.find(x => x.id == c.option);
                if (cell == null) return null;

                const { id, field } = cell;

                return field != null ? field : typeof id == 'number' ? id.toString() : null;
            })
            .filter(f => !!f) as string[];
        this.sort = this.sort.filter(s => !!fields.find(f => s.startsWith(f)));

        this.selectAllColumnsChecked =
            this.columns.length ==
            (this.viewType == DisplayViewTypes.Aggregate ? displayAggregateCells.length : displayTelemetryCells.length);
    }

    @bind
    @action
    public onSelectAllColumns() {
        this.selectAllColumnsChecked = !this.selectAllColumnsChecked;

        if (this.selectAllColumnsChecked) {
            const cells = this.viewType == DisplayViewTypes.Aggregate ? displayAggregateCells : displayTelemetryCells;

            const fields = cells
                .map(c => {
                    return { option: c.id };
                })
                .filter(f => !!f);

            this.onColumnChange(fields);
        } else {
            this.onColumnChange([{ option: 'Serial Number' }]);
        }
    }

    @bind
    @action
    public onColumnGroupSelectAll(group: string, checked: boolean) {
        const groupedCells = displayTelemetryCells.filter(c => c.group == group);

        if (!checked) {
            const fields = groupedCells
                .map(c => {
                    return { option: c.id, group: c.group } as IMultiSelectOption<string | Mri.WebApi.MeasurementId>;
                })
                .concat(this.columns.filter(c => !groupedCells.find(g => g.id == c.option)))
                .filter(f => !!f);

            this.onColumnChange(fields);
        } else {
            const filteredColumns = this.columns.filter(c => !groupedCells.find(g => g.id == c.option));
            this.onColumnChange(filteredColumns);
        }
    }

    @bind
    @action
    public clearAllColumns() {
        this.columns = [];
    }

    @bind
    @action
    public onSortChange(sort: string[]) {
        this.sort = sort;
    }

    @bind
    @action
    public onStickyColumnChange(columnName: string) {
        const stickyColumn = this.stickyColumns.find(c => c == columnName);

        if (stickyColumn) {
            this.stickyColumns.splice(this.stickyColumns.indexOf(stickyColumn), 1);
        } else {
            this.stickyColumns.unshift(columnName);
        }

        DisplayListViewModel.stores.accountStore.storage.displayTableStickyColumns = this.stickyColumns;
    }

    @bind
    @action
    public bulkUpdateStickyColumns(columns: string[]) {
        this.stickyColumns = columns;
        DisplayListViewModel.stores.accountStore.storage.displayTableStickyColumns = this.stickyColumns;
    }

    @bind
    @action
    public clearAllStickyColumns() {
        this.stickyColumns = [];

        DisplayListViewModel.stores.accountStore.storage.displayTableStickyColumns = this.stickyColumns;
    }


    @bind
    @action
    public async onDisplayViewTypeChange(event: React.FormEvent<HTMLButtonElement>, viewType: DisplayViewTypes) {
        this.viewType = viewType;
        DisplayListViewModel.stores.displayFiltersStore.storage.viewType = viewType;

        if (this.viewType == DisplayViewTypes.MapView) {
            this.sort = ['StatusRank desc'];
        } else if (this.viewType == DisplayViewTypes.TableSmall) {
            this.columns = DisplayListViewModel.stores.accountStore.storage.displayTableColumns;
            this.limit = 40;
        } else if (this.viewType == DisplayViewTypes.TableLarge) {
            this.columns = DisplayListViewModel.stores.accountStore.storage.displayTableColumns;
        } else if (this.viewType == DisplayViewTypes.Aggregate) {
            this.columns = DisplayListViewModel.stores.accountStore.storage.displayAggregateColumns;
            this.limit = 40;
            await this.reloadAsync();
        }

        this.selectAllColumnsChecked =
            this.columns.length ==
            (this.viewType == DisplayViewTypes.Aggregate ? displayAggregateCells.length : displayTelemetryCells.length);
    }

    @bind
    @action
    public onQueryChange(search: string, report = true) {
        let displayFilter: Mri.DisplayFilters.DisplayFilter | undefined;
        let displayEventFilter: Mri.DisplayEvents.DisplayEventFilter | undefined;
        let sort: string[] | undefined;
        let where: IWhereClause[] | undefined;

        const query = parse(search);
        if (query.displayFilter) {
            try {
                displayFilter = JSON.parse(asStringOrEmpty(query.displayFilter));
            } catch (e: any) {
                console.error(e);
            }
        }

        if (query.displayEventFilter) {
            try {
                displayEventFilter = JSON.parse(asStringOrEmpty(query.displayEventFilter));
            } catch (e: any) {
                console.error(e);
            }
        }

        if (query.sort) {
            try {
                sort = JSON.parse(asStringOrEmpty(query.sort));
            } catch (e: any) {
                console.error(e);
            }
        }

        if (
            (displayFilter && !mobxEqual(displayFilter, this.displayFilter)) ||
            (displayEventFilter && !mobxEqual(displayEventFilter, this.displayEventFilter)) ||
            (sort && !mobxEqual(sort, this.sort)) ||
            (where && !mobxEqual(where, this.where))
        ) {
            if (displayFilter) {
                displayFilter.addressSearch = undefined;
                displayFilter.serialNumberSearch = undefined;
                this.displayFilter = this.parseDisplayFilter(displayFilter);
            }
            if (displayFilter) this.filters = this.generateFilters();
            if (displayEventFilter) this.displayEventFilter = displayEventFilter;
            if (sort) this.sort = sort;
            if (where) this.where = where;

            if (report) this.onDisplayFilterChange();
        }
    }

    @bind
    @action
    public onFilterDropdownToggle(isOpen: boolean) {
        this.filterOpen = isOpen;

        if (!isOpen) this.consolidateFilters();
    }

    @bind
    public isHovered(display: ActiveDisplay) {
        if(this.hoveredSerialNumber){
            return this.hoveredSerialNumber == display.unitSerialNumber || this.hoveredSerialNumber == display.serialNumber;
        }
        return false;
    }

    @bind
    @action
    public onDisplayHover(display?: ActiveDisplay) {
        this.hoveredSerialNumber = display ? display.unitSerialNumber ?? display.serialNumber : undefined;
    }

    @bind
    @action
    public async toggleCheckAllAsync() {
        if (this.allChecked) {
            this.clearChecked();
        } else {
            await this.loadMoreAsync(this.total);

            this.checkedSerialNumbers = this.serialNumbers.slice();
        }
    }

    @bind
    @action
    public toggleCheck(display: ActiveDisplay) {
        const idx = this.checkedSerialNumbers.indexOf(display.serialNumber);

        if (idx != -1) this.checkedSerialNumbers.splice(idx, 1);
        else this.checkedSerialNumbers.push(display.serialNumber);
    }

    @bind
    @action
    public clearChecked() {
        this.checkedSerialNumbers = [];
    }

    @bind
    @action
    public clearDisplayEventFilter() {
        if (this.displayEventFilter) {
            this.displayEventFilter.displayEventTypes = [];
            this.displayEventFilter.displayEventStatus = [];
            this.displayEventFilter.downtimeIncidentTypes = [];
            this.displayEventFilter.current = undefined;
            this.displayEventFilter.downtimeActiveIncident = undefined;
            this.displayEventFilter.minDuration = undefined;
            this.displayEventFilter.maxDuration = undefined;
            this.displayEventFilter.startTime = undefined;
            this.displayEventFilter.endTime = undefined;
            this.displayEventFilter.detailsSearch = '';
        }
    }

    @bind
    @action
    public clearAll() {
        this.displayFilter = this.parseDisplayFilter(this.defaultDisplayFilter);
        this.filters = [];
        this.clearDisplayEventFilter();
        this.sort = [];
        this.clearChecked();

        if (this.reportClear) this.reportClear();
    }

    @bind
    @action
    public clearSort() {
        this.sort = [];
    }

    @bind
    @action
    public removeDisplay(display: ActiveDisplay) {
        this.serialNumbers = this.serialNumbers.filter(s => s != display.serialNumber);
    }

    @bind
    @action
    public pushFilter(meta: IInputMultiSelectFiltersMeta<DisplayListViewModel>) {
        this.filters.push(meta);
    }

    @bind
    @action
    public removeLastFilter() {
        this.filters.pop();
    }

    @bind
    @action
    public consolidateFilters() {
        this.filters = this.filters.filter(f => f.shouldRender(this));
        this.filters = unionWith(this.filters, this.generateFilters(), (a, b) => a.id == b.id);
    }

    @bind
    @action
    public async dispose() {
        this.isDisposed = true;

        for (const reactionDisposer of this.reactionDisposers) reactionDisposer();

        if (this.intervalHandle) window.clearTimeout(this.intervalHandle);

        if (this.connectionHandle) {
            this.connectionHandle.dispose();
            delete this.connectionHandle;
        }

        if (this.abortController) {
            this.abortController.abort();
            delete this.abortController;
        }
    }

    @bind
    @action
    public async refreshScreenshotsAsync() {
        this.refreshingSnapshots = true;
        try {
            const refreshPromises = Array.from(this.subscriptions).map(([s]) =>
                DisplayListViewModel.stores.displaysStore.loadCurrentScreenSnapshotAsync(s)
            );

            await Promise.all(refreshPromises);
        } catch (e: any) {
            console.error('Failed to refresh', e);
        }
        this.refreshingSnapshots = false;
    }

    @computed
    public get hasMore() {
        return this.serialNumbers.length != this.total || this.scrollCount < this.serialNumbers.length;
    }

    @computed
    public get allChecked() {
        return this.checkedSerialNumbers.length == this.total;
    }

    @computed
    public get displays() {
        return this.serialNumbers
            .map(s => DisplayListViewModel.stores.displaysStore.displays.get(s))
            .filter(d => !!d) as ActiveDisplay[];
    }

    @computed
    public get checkedDisplays() {
        return this.checkedSerialNumbers.map(s =>
            DisplayListViewModel.stores.displaysStore.displays.get(s)
        ) as ActiveDisplay[];
    }

    @computed
    private get status() {
        return (
            this.columns.some(c => c.option == 'New Events') ||
            this.columns.some(c => c.option == 'OpenTicketCount') ||
            this.columns.some(c => c.option == 'Last Poll Time') ||
            this.columns.some(c => c.option == 'Status') ||
            this.columns.some(c => c.option == 'Status Brief') ||
            this.columns.some(c => c.option == 'Ship Date')
        );
    }

    @computed
    private get telemetry() {
        return this.columns.find(c => typeof c == 'number') != undefined;
    }

    @computed
    private get hasScreenSnapshot() {
        return this.columns.find(c => c.option == 'PopVu™' || c.option == 'InfiniteTouch®') != undefined; //TEST
    }

    @computed
    private get aggregates() {
        return this.viewType == DisplayViewTypes.Aggregate;
    }

    @computed
    private get gpsSearch() {
        const search: Mri.Web.Portal.Controllers.Displays.GpsSearchModel = {
            searchGuid: this.searchGuid,
            displayFilter: this.displayFilter,
            displayEventFilter: this.displayEventFilter,
            favorites: this.favorites,
        };

        return search;
    }

    @bind
    @action
    private async tryDeferDisplayFilterChange() {
        if (this.filterOpen) this.deferOnDisplayFilterChange = true;
        else await this.onDisplayFilterChange();
    }

    @bind
    @action
    private async handleDeferredDisplayFilterChange() {
        if (!this.filterOpen && this.deferOnDisplayFilterChange) {
            this.deferOnDisplayFilterChange = false;
            await this.onDisplayFilterChange();
        }
    }

    @bind
    @action
    public async onDisplayFilterChange() {
        this.serialNumbers = [];
        this.consolidateFilters();
        if (this.removeCheckedOnChange != false) this.checkedSerialNumbers = [];

        this.total = -1;
        this.scrollCount = 40;
        this.searchGuid = uuidv4generate();

        const p1 = this.loadMoreAsync();
        const p2 = this.loadOptionsAsync();
        const p3 = this.viewType == DisplayViewTypes.MapView ? this.loadGpsAsync() : undefined;

        await Promise.all([p1, p2, p3]);

        if (this.reportChange)
            this.reportChange(
                this.displayFilter, 
                this.displayEventFilter, 
                this.sort, 
                this.where,
                this.columns, 
                this.viewType
            );
    }

    @bind
    private async subscribeDisplaysAsync(serialNumbers: readonly string[]): Promise<void> {
        await this.subscribeLock.waitAsync();
        try {
            if (!this.connectionHandle)
                this.connectionHandle = await DisplayListViewModel.stores.displaysStore.connectionManager.connect();

            const subscribePromises = serialNumbers
                .filter(s => this.subscriptions.get(s) != true)
                .map(async s => {
                    await DisplayListViewModel.stores.displaysStore.subscribeToDisplayAsync(s);
                    this.subscriptions.set(s, true);
                    await DisplayListViewModel.stores.displaysStore.refresh(s);
                    if (this.hasScreenSnapshot)
                        await DisplayListViewModel.stores.displaysStore.refreshScreenSnapshotAsync(s);
                });

            const unsubscribePromises = Array.from(this.subscriptions)
                .filter(([s, v]) => v && !serialNumbers.includes(s))
                .map(async ([s]) => {
                    await DisplayListViewModel.stores.displaysStore.unsubscribeToDisplayAsync(s);
                    this.subscriptions.delete(s);
                });

            await Promise.all([...subscribePromises, ...unsubscribePromises]);
        } catch (e: any) {
            console.error('Failed to subscribe', e);
        } finally {
            this.subscribeLock.release();
        }
    }

    @bind
    private async refreshAsync() {
        try {
            const refreshPromises = Array.from(this.subscriptions).map(([s]) =>
                DisplayListViewModel.stores.displaysStore.refresh(s)
            );

            await Promise.all(refreshPromises);

            this.intervalHandle = window.setTimeout(this.refreshAsync, this.interval);
        } catch (e: any) {
            console.error('Failed to refresh', e);
        }
    }

    private parseDisplayFilter(
        displayFilter?: Optional<Mri.DisplayFilters.DisplayFilter>
    ): Mri.DisplayFilters.DisplayFilter {
        const df = displayFilter ?? {};

        return {
            accounts: undefinedIfEmpty(df.accounts),
            active: df.active != undefined ? df.active : undefined,
            addressSearch: df.addressSearch,
            cities: undefinedIfEmpty(df.cities),
            stateOrProvinces: undefinedIfEmpty(df.stateOrProvinces),
            countries: undefinedIfEmpty(df.countries),
            contracts: undefinedIfEmpty(df.contracts),
            contractOptions: undefinedIfEmpty(df.contractOptions),
            contractServiceLevels: undefinedIfEmpty(df.contractServiceLevels),
            currentSoftwareVersions: undefinedIfEmpty(df.currentSoftwareVersions),
            displayGroups: undefinedIfEmpty(df.displayGroups),
            displays: undefinedIfEmpty(df.displays),
            nocServers: undefinedIfEmpty(df.nocServers),
            partDefinitions: undefinedIfEmpty(df.partDefinitions),
            productionProjects: undefinedIfEmpty(df.productionProjects),
            userIds: undefinedIfEmpty(df.userIds),
            serialNumberSearch: df.serialNumberSearch,
            textSearch: df.textSearch,
        };
    }

    private generateFilters(): IInputMultiSelectFiltersMeta<DisplayListViewModel>[] {
        return displayFilters.filter(f => f.shouldRender(this));
    }

    @observable
    public startDate: Date = new Date();
    @observable
    public endDate: Date = new Date();
    @observable
    public refreshingSnapshots: boolean = false;
    @observable
    public displayFilter: Mri.DisplayFilters.DisplayFilter;
    @observable
    public exceptDisplayFilter?: Mri.DisplayFilters.DisplayFilter;
    @observable
    public displayEventFilter?: Mri.DisplayEvents.DisplayEventFilter;
    @observable
    public columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    @observable
    public draggingStickyIdx: number = 0;
    @observable
    public droppedHeaders: string[] = [];
    @observable
    public draggedHeaderId: string = '';
    @observable
    public draggedHeaderWidth: number = 0;
    @observable
    public stickyColumns: string[];
    @observable
    public stickyOffsets: IDisplayCellOffset[];
    @observable
    public showStickyColumns?: boolean = false;
    @observable
    public stickyColumnDropdownOpen: boolean = false;
    @observable
    public filters: IInputMultiSelectFiltersMeta<DisplayListViewModel>[] = [];
    @observable
    public disabledFilters: string[] = [];
    @observable
    public sort: string[] = [];
    @observable
    public where?: IWhereClause[];
    @observable
    public filterOptions?: Mri.DisplayFilters.DisplayFilterOptions;
    @observable
    public listAggregates?: Mri.Web.Common.Controllers.DisplayListAggregatesModel;
    @observable
    public total = -1;
    @observable
    public scrollCount = 40;
    @observable
    public busy = false;
    @observable
    public headerRowRef: HTMLTableRowElement | null = null;
    @observable
    public searchedColumn: string = '';
    @observable
    public viewType: DisplayViewTypes = DisplayViewTypes.TableLarge;
    @observable
    public filterOpen = false;
    @observable
    public hoveredSerialNumber?: string;
    @observable
    public error?: any;
    @observable
    public selectAllColumnsChecked: boolean = false;
    public isDisposed = false;

    @observable
    private serialNumbers: readonly string[] = [];
    @observable
    private checkedSerialNumbers: string[] = [];
    private deferOnDisplayFilterChange = false;
    private subscriptions = new Map<string, boolean>();
    private abortController?: AbortController;
    private readonly reportChange?: (
        displayFilter: Mri.DisplayFilters.DisplayFilter,
        displayEventFilter: Mri.DisplayEvents.DisplayEventFilter | undefined,
        sort: string[],
        where: IWhereClause[] | undefined,
        columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[],
        viewType: DisplayViewTypes
    ) => void;
    private readonly reportClear?: () => void;
    private readonly loadDisplaysLock = new Mutex();
    private readonly subscribeLock = new Mutex();
    private readonly subscribe?: boolean;
    private readonly interval?: number;
    private intervalHandle?: number;
    private readonly removeCheckedOnChange?: boolean;
    private readonly defaultDisplayFilter: Readonly<Optional<Mri.DisplayFilters.DisplayFilter>>;
    private searchGuid = uuidv4generate();
    private limit = 40;
    private readonly favorites: boolean;
    private readonly reactionDisposers: IReactionDisposer[] = [];
    private readonly retryPolicy = new RetryPolicy<Mri.WebApi.PagingResult<string> | undefined>(
        2,
        () => 3000,
        undefined,
        e => e.exceptionType == 'Overloaded'
    );
    private readonly optionsRetryPolicy = new RetryPolicy(
        2,
        () => 3000,
        undefined,
        e => e.exceptionType == 'Overloaded'
    );
    private connectionHandle?: IDisposable;
}
